import styled, { css, CSSObject } from 'styled-components';
import type {
    TButtonSize,
    HBButtonCustomTheme,
    TScopeSizeMap,
    HBButtonTheme
} from './HBButton.types';
import colors from '../../styles/colors';
import { applyOpacity, darken, opacityHex } from '../../utils/color.utils';
import { absoluteCentered, aiButtonGradient } from '../../styles/mixins';
import { customFont, fontWeights } from '../../styles/fonts';
import spacing from '../../styles/spacing';
import { transition } from '../../styles/animations';
import { hoverEase } from '../../styles/variables';
import HBLoader from '../HBLoader/HBLoader';
import { TScope, CSSDimensionValue } from '../../tokens/types';
import { vars } from '../../theme';

/* Drop shadow */
const elevation = {
    none: 'none'
} as const;

/* Disabled state opacity */
const disabledBgOpacity = {
    '0': 15,
    '1': 25
} as const;

const iconSpacing: Partial<Record<TButtonSize, string>> = {
    xs: spacing[4],
    sm: spacing[8],
    md: spacing[8],
    lg: spacing[8]
} as const;

/* Button sizes */
export const buttonSizes: TScopeSizeMap<
    Partial<CSSObject> & {
        height: string;
    }
> = {
    system: {
        xs: {
            fontSize: '14px',
            lineHeight: '1',
            height: '30px',
            padding: '1px 12px 0px'
        },

        sm: {
            fontSize: '14px',
            lineHeight: '1',
            padding: '1px 16px 0px',
            height: '36px'
        },

        md: {
            fontSize: '16px',
            lineHeight: '1',
            height: '42px',
            padding: '1px 20px 0'
        },

        lg: {
            fontSize: '18px',
            lineHeight: '1',
            height: '48px',
            padding: '1px 24px 0px'
        }
    },
    builder: {
        sm: {
            fontSize: '16px',
            padding: '0 14px',
            height: '40px'
        },

        md: {
            fontSize: '18px',
            padding: '0 14px',
            height: '42px'
        },

        xl: {
            fontSize: '18px',
            padding: '0 14px',
            height: '48px'
        }
    }
} as const;

export const HBBUTTON_BORDER_RADIUS = '2px';

export const buttonBorderRadius: Record<TButtonSize, string> = {
    xs: '4px',
    sm: '4px',
    md: '5px',
    lg: '6px',
    xl: '6px'
} as const;

/* Button themes */

const primaryStyles = {
    color: vars.colors.text.invertedPrimary,
    fontWeight: fontWeights.semibold,
    hoverColor: vars.colors.text.invertedPrimary,
    boxShadow: elevation.none,
    splitterBackground: vars.colors.surface.itemInvertedPressed,
    hoverBoxShadow: elevation.none,
    disabledBgOpacity: disabledBgOpacity[1]
} as const;

const baseSecondaryStyles = {
    fontWeight: fontWeights.semibold,
    disabledBgOpacity: disabledBgOpacity[1]
} as const;

const secondarySolidStyles = {
    ...baseSecondaryStyles,
    background: vars.colors.surface.item,
    hoverBackground: vars.colors.surface.item,
    openMenuButtonColor: vars.colors.surface.itemHover,
    splitterBackground: vars.colors.stroke.primary,
    boxShadow: `inset 0 0 0 1px ${vars.colors.stroke.primary}`,
    hoverBoxShadow: `inset 0 0 0 1px ${vars.colors.stroke.primary}`,
    disabledBoxShadow: `inset 0 0 0 1px ${vars.colors.stroke.disabled}`
} as const;

const secondaryInvisibleStyles = {
    ...baseSecondaryStyles,
    background: 'transparent',
    boxShadow: elevation.none,
    hoverBoxShadow: elevation.none,
    disabledBgOpacity: disabledBgOpacity[0]
} as const;

const secondaryStyles = {
    color: vars.colors.text.invertedPrimary,
    fontWeight: fontWeights.semibold,
    hoverColor: vars.colors.text.invertedPrimary,
    boxShadow: elevation.none,
    hoverBoxShadow: elevation.none,
    disabledBgOpacity: disabledBgOpacity[1]
} as const;

export const buttonThemes = {
    primary: {
        background: vars.colors.surface.itemInverted,
        hoverBackground: vars.colors.surface.itemInvertedHover,
        splitterBackground: applyOpacity(
            vars.colors.surface.containerInverted,
            70
        ),
        splitterHoverBackground: vars.colors.surface.containerInverted,
        ...primaryStyles
    },

    'primary-blue': {
        background: vars.colors.base.accent,
        hoverBackground: vars.colors.base.accentHover,
        splitterBackground: applyOpacity(vars.colors.base.accentHover, 70),
        splitterHoverBackground: vars.colors.base.accentHover,
        ...primaryStyles
    },

    'primary-alternative': {
        background: vars.colors.base.accent,
        hoverBackground: vars.colors.base.accentHover,
        splitterBackground: applyOpacity(vars.colors.base.accentHover, 70),
        splitterHoverBackground: vars.colors.base.accentHover,
        ...primaryStyles
    },

    'primary-alpine': {
        background: vars.colors.base.upgrade,
        hoverBackground: vars.colors.base.upgradeHover,
        ...primaryStyles
    },

    'secondary-ai': {
        ...secondarySolidStyles,
        color: vars.colors.text.primary,
        hoverColor: vars.colors.text.primary,
        disabledColor: applyOpacity(vars.colors.text.primary, 25),
        background: aiButtonGradient,
        hoverBackground: vars.colors.surface.itemHover
    },

    'secondary-solid-primary': {
        ...secondarySolidStyles,
        color: vars.colors.text.primary,
        hoverColor: vars.colors.text.primary,
        disabledColor: applyOpacity(vars.colors.text.primary, 25),
        hoverBackground: vars.colors.surface.itemHover
    },

    'secondary-solid-secondary': {
        ...secondarySolidStyles,
        color: vars.colors.text.primary,
        hoverColor: vars.colors.text.primary,
        disabledColor: vars.colors.text.disabled,
        hoverBackground: vars.colors.surface.itemHover
    },

    'secondary-solid-primary-alternative': {
        ...secondarySolidStyles,
        color: vars.colors.text.accent,
        hoverColor: vars.colors.text.accentHover,
        disabledColor: applyOpacity(vars.colors.text.accent, 50),
        hoverBackground: vars.colors.surface.accentHover
    },

    'secondary-invisible-alternative': {
        color: vars.colors.text.accent,
        hoverColor: vars.colors.text.accentHover,
        disabledColor: applyOpacity(vars.colors.text.accent, 50),
        ...secondaryInvisibleStyles
    },

    'secondary-solid-destructive': {
        ...secondarySolidStyles,
        color: vars.colors.text.danger,
        hoverColor: vars.colors.text.dangerHover,
        disabledColor: applyOpacity(vars.colors.text.danger, 25),
        hoverBackground: vars.colors.surface.dangerHover
    },

    'secondary-invisible-primary': {
        color: vars.colors.text.primary,
        hoverColor: vars.colors.text.primary,
        hoverBackground: applyOpacity(vars.colors.surface.itemInverted, 8),
        disabledColor: applyOpacity(vars.colors.text.primary, 50),
        selectedBackground: vars.colors.surface.itemSelectedPressed,
        ...secondaryInvisibleStyles
    },

    'secondary-invisible-secondary': {
        color: vars.colors.text.primary,
        disabledColor: applyOpacity(vars.colors.text.primary, 50),
        hoverColor: vars.colors.text.primary,
        hoverBackground: vars.colors.surface.neutral,
        selectedBackground: vars.colors.surface.itemSelectedPressed,
        ...secondaryInvisibleStyles
    },

    'secondary-invisible-destructive': {
        color: vars.colors.text.danger,
        disabledColor: applyOpacity(vars.colors.text.danger, 35),
        hoverColor: vars.colors.text.danger,
        hoverBackground: vars.colors.surface.danger,
        ...secondaryInvisibleStyles
    },

    'secondary-destructive': {
        background: vars.colors.base.danger,
        hoverBackground: vars.colors.base.dangerHover,
        splitterBackground: applyOpacity(vars.colors.base.dangerHover, 70),
        splitterHoverBackground: vars.colors.base.dangerHover,
        ...secondaryStyles
    },

    'secondary-translucent': {
        background: applyOpacity(vars.colors.surface.itemInverted, 70),
        hoverBackground: applyOpacity(vars.colors.surface.itemInverted, 80),
        splitterBackground: applyOpacity(vars.colors.surface.itemInverted, 50),
        splitterHoverBackground: applyOpacity(
            vars.colors.surface.itemInverted,
            80
        ),
        disabledColor: applyOpacity(vars.colors.text.invertedPrimary, 40),
        ...secondaryStyles
    },

    'secondary-translucent-secondary': {
        background: 'transparent',
        hoverBackground: applyOpacity(vars.colors.surface.item, 20),
        splitterBackground: applyOpacity(vars.colors.surface.itemInverted, 70),
        splitterHoverBackground: vars.colors.surface.itemInverted,
        ...secondaryInvisibleStyles
    },

    'secondary-alpine': {
        background: vars.colors.base.upgrade,
        hoverBackground: vars.colors.base.upgradeHover,
        ...secondaryStyles
    },

    'button-link': {
        color: vars.colors.text.primary,
        hoverColor: vars.colors.text.primary,
        hoverBackground: 'transparent',
        disabledColor: applyOpacity(vars.colors.text.disabled, 5),
        ...secondaryInvisibleStyles
    },

    'button-link-solid': {
        color: vars.colors.text.primary,
        hoverColor: vars.colors.text.primary,
        hoverBackground: 'transparent',
        disabledColor: applyOpacity(vars.colors.text.disabled, 5),
        ...secondaryInvisibleStyles
    },

    'button-link-secondary': {
        hoverBackground: 'transparent',
        disabledColor: applyOpacity(vars.colors.text.disabled, 5),
        ...secondaryInvisibleStyles
    },

    'button-link-blue': {
        color: vars.colors.text.accent,
        hoverColor: vars.colors.text.accentHover,
        hoverBackground: 'transparent',
        disabledColor: applyOpacity(vars.colors.text.accent, 50),
        ...secondaryInvisibleStyles
    },

    'button-link-alpine': {
        color: vars.colors.text.upgrade,
        hoverColor: vars.colors.text.upgradeHover,
        hoverBackground: 'transparent',
        disabledColor: applyOpacity(vars.colors.text.upgrade, 25),
        ...secondaryInvisibleStyles
    },

    'link-button-accent': {
        color: vars.colors.text.invertedPrimary,
        hoverColor: applyOpacity(vars.colors.text.invertedPrimary, 85),
        hoverBackground: 'transparent',
        disabledColor: applyOpacity(vars.colors.base.accent, 50),
        ...secondaryInvisibleStyles
    },

    'link-button-neutral': {
        color: vars.colors.text.primary,
        hoverColor: vars.colors.text.primary,
        hoverBackground: 'transparent',
        disabledColor: vars.colors.text.disabled,
        ...secondaryInvisibleStyles
    },

    'primary-inverted': {
        background: vars.colors.surface.item,
        hoverBackground: vars.colors.surface.itemStrong,
        color: vars.colors.text.primary,
        hoverColor: vars.colors.text.primary,
        disabledColor: vars.colors.text.primary,
        fontWeight: fontWeights.semibold,
        boxShadow: elevation.none,
        hoverBoxShadow: elevation.none,
        disabledBgOpacity: disabledBgOpacity[1]
    },

    'secondary-inverted': {
        background: 'transparent',
        hoverBackground: 'transparent',
        color: vars.colors.text.invertedPrimary,
        hoverColor: vars.colors.text.invertedPrimary,
        disabledColor: applyOpacity(vars.colors.text.invertedPrimary, 25),
        fontWeight: fontWeights.semibold,
        boxShadow: `inset 0 0 0 1px ${vars.colors.stroke.primary}`,
        hoverBoxShadow: `inset 0 0 0 1px ${vars.colors.stroke.tertiary}`,
        disabledBoxShadow: `inset 0 0 0 1px ${applyOpacity(
            vars.colors.stroke.primary,
            25
        )}`,
        disabledBgOpacity: disabledBgOpacity[0]
    },

    custom: {
        disabledBgOpacity: disabledBgOpacity[1]
    }
} as const;

export function getButtonBorderRadius(
    borderRadius: number,
    buttonSize: TButtonSize,
    scope: TScope = 'system'
) {
    const percentBorderRadius = borderRadius / 100;
    return (
        percentBorderRadius *
        (Number(buttonSizes[scope][buttonSize].height.replace('px', '')) / 2)
    );
}

function getIconFillStyle(
    isHover: boolean,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    shouldUseIconDefaultFill?: boolean
) {
    return css`
        i:not([data-hbui-preserve-fill]) svg * {
            fill: currentColor;
            ${isHover ? '' : transition('fill', ...hoverEase)};
        }
    `;
}

function getBorderFromTheme(theme: string) {
    const { borderColor, borderRadius, disabledBorderColor } =
        buttonThemes[theme] || {};
    return css`
        ${borderColor &&
        css`
            border: 1px solid ${borderColor};
            &:disabled,
            &:disabled:hover {
                border: 1px solid ${disabledBorderColor};
            }
        `}
        ${borderRadius &&
        css`
            border-radius: ${borderRadius};
        `}
    `;
}

function getButtonTheme(theme: string, shouldUseIconDefaultFill: boolean) {
    if (!buttonThemes[theme]) {
        return null;
    }

    const {
        color,
        fontWeight,
        hoverColor,
        background,
        hoverBackground,
        boxShadow,
        hoverBoxShadow
    } = buttonThemes[theme];

    function getButtonHoverState() {
        return css`
            color: ${hoverColor};
            background: ${hoverBackground} !important;
            box-shadow: ${hoverBoxShadow};
            ${getBorderFromTheme(theme)}
            ${getIconFillStyle(true, shouldUseIconDefaultFill)};
        `;
    }

    return css`
        &,
        &:focus {
            font-family: ${vars.fonts.body};
            font-weight: ${fontWeight};
            background: ${background} !important;
            color: ${color};
            box-shadow: ${boxShadow};
            ${getBorderFromTheme(theme)}
            ${getIconFillStyle(false, shouldUseIconDefaultFill)};
        }

        &:hover:not(:disabled) {
            ${getButtonHoverState()}
        }
        &:focus-visible:not(:disabled) {
            ${getButtonHoverState()}
        }
    `;
}

function getSelectedStyles(theme: string) {
    const { selectedBackground, color } = buttonThemes[theme] || {};
    if (!selectedBackground) {
        return null;
    }

    return css`
        &&,
        &&:hover,
        &&:focus {
            background: ${selectedBackground} !important;
            color: ${color};
        }
    `;
}

function getDisabledStyles(theme: string, customTheme?: HBButtonCustomTheme) {
    const {
        disabledBgOpacity: _disabledBgOpacity,
        disabledColor,
        disabledBoxShadow,
        background
    } = buttonThemes[theme] || {};

    function getBackground() {
        const calculatedBackground =
            theme === 'custom' ? customTheme.background : background;

        if (calculatedBackground === 'transparent') {
            return background;
        }

        return calculatedBackground;
    }

    function calculateBackground() {
        const calculatedBackground = getBackground();

        if (calculatedBackground === 'transparent' || !calculatedBackground) {
            return calculatedBackground;
        }

        // applyOpacity (color-mix) instead of opacityHex — theme backgrounds
        // are CSS variables, which opacityHex cannot parse
        return applyOpacity(calculatedBackground, _disabledBgOpacity);
    }

    const color = theme === 'custom' ? customTheme.color : disabledColor;
    const calculatedBackground = calculateBackground();

    /*
        This function is a bit of a mess.. There are 3 parameters that set the disabled styles:
        disabledColor - this is used for buttons with an invisible theme (ie - no background color). When disabled, the text changes color.
        background color - unless transparent/white, we add an opacity to the background color.
    */
    return css`
        &:disabled,
        &:disabled:hover {
            border: none;
            ${getBorderFromTheme(theme)}
            ${color && `color: ${color} !important;`}
            box-shadow: ${disabledBoxShadow || 'none'} !important;
            ${calculatedBackground &&
            `background: ${calculatedBackground} !important;`}
            cursor: default;
        }
    `;
}

function getCustomTheme(customTheme: HBButtonCustomTheme) {
    const {
        color = vars.colors.text.invertedPrimary,
        background = vars.colors.surface.itemInverted,
        fontFamily,
        fontStyle = 'normal',
        fontWeight = 600,
        fontSize,
        border,
        borderRadius,
        hoverBackgroundColor,
        hoverColor,
        boxShadow,
        hoverBoxShadow
    } = customTheme;

    function getHoverBackgroundColor() {
        if (hoverBackgroundColor) {
            return hoverBackgroundColor;
        }
        return background === 'transparent'
            ? 'transparent'
            : darken(background, 0.3);
    }

    function getCustomButtonHoverState() {
        return css`
            background: ${getHoverBackgroundColor()};
            color: ${hoverColor || color};
            box-shadow: ${hoverBoxShadow};
            ${getIconFillStyle(true)};
        `;
    }

    // !important on color is because of foundation overrides
    return css`
        &,
        &:focus {
            color: ${color};
            background: ${background};
            font-family: ${customFont[fontFamily]?.font_family ||
            fontFamily ||
            `inherit`};
            font-style: ${fontStyle};
            font-weight: ${fontWeight};
            font-size: ${fontSize};
            box-shadow: ${boxShadow};
            ${border && `border: ${border};`}
            ${borderRadius && `border-radius: ${borderRadius};`}
            ${getIconFillStyle(false)};
        }

        &:hover {
            ${getCustomButtonHoverState()}
        }
        &:focus-visible {
            ${getCustomButtonHoverState()}
        }
    `;
}

export interface Props {
    size: TButtonSize;
    scope: TScope;
    theme: HBButtonTheme;
    fullWidth?: boolean;
    customTheme?: HBButtonCustomTheme;
    $margin: CSSDimensionValue;
    $shouldUseIconDefaultFill?: boolean;
    $isSelected?: boolean;
}

export const IconWrapper = styled.span<{
    iconPosition?: 'start' | 'end';
    size: TButtonSize;
}>`
    ${({ iconPosition, size }) =>
        iconPosition === 'start' &&
        css`
            margin-right: ${iconSpacing[size]};
        `}

    ${({ iconPosition, size }) =>
        iconPosition === 'end' &&
        css`
            order: 1;
            margin-left: ${iconSpacing[size]};
        `}
    i {
        display: flex;
    }
`;

// Shared styles for both button and anchor elements
const buttonBaseStyles = css<Props>`
    position: relative;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    user-select: none;
    border-radius: ${({ size }) =>
        buttonBorderRadius[size] || HBBUTTON_BORDER_RADIUS};
    border: none;
    letter-spacing: 0;
    cursor: pointer;
    text-decoration: none; /* For anchor elements */
    white-space: nowrap;
    ${transition('all', ...hoverEase)};
    ${({ scope, size }) => buttonSizes[scope][size]};
    ${({ customTheme, theme, $shouldUseIconDefaultFill, $isSelected }) =>
        !customTheme &&
        css`
            ${getButtonTheme(theme, $shouldUseIconDefaultFill)}
            ${getDisabledStyles(theme)}
            ${$isSelected && getSelectedStyles(theme)}
        `}
    ${({ fullWidth }) =>
        fullWidth &&
        css`
            width: 100%;
        `}

    margin: ${({ $margin }) => $margin};
    &:hover {
        background: ${({ theme }) => theme.backgroundHover};
    }
`;

export const Button = styled.button<Props>`
    ${buttonBaseStyles}
`;

export const Anchor = styled.a<Props>`
    ${buttonBaseStyles}
`;

export const CustomButton = styled(Button)`
    ${({ customTheme }) => getCustomTheme(customTheme)}
    ${({ customTheme }) => getDisabledStyles('custom', customTheme)}
`;

// Override HBLoader styles
export const ButtonLoader = styled(HBLoader)`
    ${absoluteCentered};
    z-index: unset;
`;
